import Phaser from 'phaser';

import type {
  MainSceneLike,
  NameLabelContainer,
} from '../types/scene';

import {
  getSpacePlayerPresentation,
} from '../../spaceWork/teamProfiles';


let labelTexId = 0;


function setRetinaResolution(
  text: Phaser.GameObjects.Text,
  dpr: number,
) {

  try {
    text.setResolution(dpr);
  } catch {
    /* noop */
  }

  try {
    text.setPadding(0, 0, 0, 1);
  } catch {
    /* noop */
  }

}


function layoutLabel(
  container: NameLabelContainer,
): void {

  const nameText = container.text;
  const metaText = container.metaText;

  if (!nameText) return;


  const paddingX =
    container.paddingX ?? 10;

  const paddingY =
    container.paddingY ?? 6;

  const gap =
    metaText ? 2 : 0;


  const contentWidth =
    Math.max(
      nameText.width,
      metaText?.width ?? 0,
    );


  const contentHeight =
    nameText.height +
    (metaText
      ? gap + metaText.height
      : 0);


  container.width =
    contentWidth +
    paddingX * 2;

  container.height =
    contentHeight +
    paddingY * 2;


  if (metaText) {

    nameText.setPosition(
      0,
      -contentHeight / 2 +
        nameText.height / 2,
    );

    metaText.setPosition(
      0,
      contentHeight / 2 -
        metaText.height / 2,
    );

  } else {

    nameText.setPosition(0, 0);

  }

}


export function createNameLabel(
  scene: MainSceneLike,
  name: string,
  playerId?: string,
  isNpc?: boolean,
): Phaser.GameObjects.Container {

  const container =
    scene.add.container(
      0,
      0,
    ) as NameLabelContainer;


  const presentation =
    isNpc
      ? {
          displayName: name,
          meta: 'BOT',
        }
      : getSpacePlayerPresentation(name);


  const dpr =
    window.devicePixelRatio || 1;


  const nameText =
    scene.add.text(
      0,
      0,
      presentation.displayName,
      {
        fontSize: '15px',

        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

        color: '#ffffff',

        fontStyle: 'normal',

        fontWeight: '600',

        align: 'center',
      },
    );


  nameText.setOrigin(
    0.5,
    0.5,
  );

  setRetinaResolution(
    nameText,
    dpr,
  );


  const metaText =
    scene.add.text(
      0,
      0,
      presentation.meta,
      {
        fontSize: '10px',

        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

        color:
          isNpc
            ? '#a78bfa'
            : '#cbd5e1',

        fontStyle: 'normal',

        fontWeight: '500',

        align: 'center',
      },
    );


  metaText.setOrigin(
    0.5,
    0.5,
  );

  setRetinaResolution(
    metaText,
    dpr,
  );


  container.text =
    nameText;

  container.metaText =
    metaText;

  container.playerId =
    playerId;

  container.isNpc =
    !!isNpc;

  container.paddingX = 10;
  container.paddingY = 6;


  layoutLabel(container);


  const bgSprite =
    renderBgTexture(
      scene,
      container,
      false,
    );


  container.add(bgSprite);
  container.add(nameText);
  container.add(metaText);

  container.setDepth(12);


  try {
    scene.labelLayer?.add(
      container,
    );
  } catch {
    /* noop */
  }


  return container;
}


function renderBgTexture(
  scene: MainSceneLike,
  container: NameLabelContainer,
  isSpeaking: boolean,
): Phaser.GameObjects.Image {

  const dpr =
    window.devicePixelRatio || 1;

  const width =
    container.width;

  const height =
    container.height;


  const w =
    Math.round(width);

  const h =
    Math.round(height);

  const sw =
    Math.ceil(w * dpr);

  const sh =
    Math.ceil(h * dpr);

  const radius =
    Math.floor(9 * dpr);


  const gfx =
    scene.add.graphics();


  // Fundo preto da identidade Space Work.
  gfx.fillStyle(
    0x111111,
    isSpeaking
      ? 0.94
      : 0.86,
  );

  gfx.fillRoundedRect(
    0,
    0,
    sw,
    sh,
    radius,
  );


  if (isSpeaking) {

    // Vermelho da marca Space quando a pessoa está falando.
    gfx.lineStyle(
      Math.max(
        1,
        Math.round(1.5 * dpr),
      ),
      0xff5151,
      1,
    );

    gfx.strokeRoundedRect(
      0,
      0,
      sw,
      sh,
      radius,
    );

  } else {

    gfx.lineStyle(
      Math.max(
        1,
        Math.round(1 * dpr),
      ),
      0xffffff,
      0.12,
    );

    gfx.strokeRoundedRect(
      0,
      0,
      sw,
      sh,
      radius,
    );

  }


  const prevKey =
    container.bgTexKey;


  if (
    prevKey &&
    scene.textures.exists(prevKey)
  ) {

    scene.textures.remove(
      prevKey,
    );

  }


  const texKey =
    `space_lbl_${++labelTexId}`;


  gfx.generateTexture(
    texKey,
    sw,
    sh,
  );

  gfx.destroy();


  container.bgTexKey =
    texKey;


  let bgSprite =
    container.bgSprite;


  if (bgSprite) {

    bgSprite.setTexture(
      texKey,
    );

  } else {

    bgSprite =
      scene.add.image(
        0,
        0,
        texKey,
      );

    container.bgSprite =
      bgSprite;

  }


  bgSprite.setOrigin(
    0.5,
    0.5,
  );

  bgSprite.setScale(
    1 / dpr,
  );


  return bgSprite;
}


export function drawNameLabel(
  scene: MainSceneLike,
  container:
    Phaser.GameObjects.Container,
  isSpeaking: boolean,
): void {

  const label =
    container as NameLabelContainer;


  const bgSprite =
    renderBgTexture(
      scene,
      label,
      isSpeaking,
    );


  if (
    label.getIndex(bgSprite) === -1
  ) {

    label.addAt(
      bgSprite,
      0,
    );

  }

}


export function updateNameLabelName(
  scene: MainSceneLike,
  container:
    Phaser.GameObjects.Container,
  name: string,
): void {

  const label =
    container as NameLabelContainer;

  const nameText =
    label.text;

  const metaText =
    label.metaText;


  if (!nameText) return;


  const presentation =
    label.isNpc
      ? {
          displayName: name,
          meta: 'BOT',
        }
      : getSpacePlayerPresentation(name);


  let changed = false;


  if (
    nameText.text !==
    presentation.displayName
  ) {

    nameText.setText(
      presentation.displayName,
    );

    changed = true;

  }


  if (
    metaText &&
    metaText.text !==
      presentation.meta
  ) {

    metaText.setText(
      presentation.meta,
    );

    changed = true;

  }


  if (!changed) return;


  layoutLabel(label);

  drawNameLabel(
    scene,
    label,
    false,
  );
}


export function updateNameLabel(
  scene: MainSceneLike,
  container:
    Phaser.GameObjects.Container,
  x: number,
  y: number,
): void {

  const cam =
    scene.cameras.main;

  const view =
    cam.worldView;


  const screenX =
    (x - view.x) *
    cam.zoom;

  const screenY =
    (y - view.y) *
    cam.zoom;


  const avatarWorldHeight = 24;

  // Label agora tem duas linhas,
  // então sobe um pouco mais.
  const baseGap = 12;


  const offsetY =
    (
      avatarWorldHeight / 2 +
      baseGap
    ) *
    cam.zoom;


  container.setPosition(
    Math.round(screenX),

    Math.round(
      screenY -
      offsetY
    ),
  );

}


export function setHeroName(
  scene: MainSceneLike,
  name: string,
): void {

  if (
    scene.heroNameLabel
  ) {

    try {
      scene.heroNameLabel.destroy();
    } catch {}

  }


  scene.heroNameLabel =
    createNameLabel(
      scene,
      name,
      'local',
    );


  if (scene.hero) {

    updateNameLabel(
      scene,
      scene.heroNameLabel,
      scene.hero.x,
      scene.hero.y,
    );

  }

}


export function updateSpeakingStates(
  scene: MainSceneLike,
  speakingIds: Set<string>,
): void {

  scene.nameLabels?.forEach(
    (
      label:
        Phaser.GameObjects.Container,
      id: string,
    ) => {

      drawNameLabel(
        scene,
        label,
        speakingIds.has(id),
      );

    },
  );


  if (
    scene.heroNameLabel
  ) {

    drawNameLabel(
      scene,
      scene.heroNameLabel,
      speakingIds.has('local'),
    );

  }

}
